import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Shield,
  Key,
  AlertTriangle,
  CheckCircle,
  Clock,
  Globe
} from 'lucide-react';

export function SecuritySettings() {
  const securitySettings = {
    twoFactorAuth: false,
    ssoEnabled: false,
    passwordPolicy: true,
    sessionTimeout: 30,
    ipRestriction: false,
    auditLogging: true
  };

  const recentActivity = [
    {
      action: 'User login',
      user: 'john.doe@example.com',
      ip: '192.168.1.100',
      timestamp: '2024-01-15 10:30 AM',
      status: 'success'
    },
    {
      action: 'Password changed',
      user: 'sarah.coach@example.com',
      ip: '10.0.1.50',
      timestamp: '2024-01-14 2:15 PM',
      status: 'success'
    },
    {
      action: 'Failed login attempt',
      user: 'unknown@domain.com',
      ip: '203.0.113.0',
      timestamp: '2024-01-13 11:45 PM',
      status: 'failed'
    }
  ];

  return (
    <div className="space-y-6">
      {/* Authentication */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Shield className="mr-2 h-5 w-5" />
            Authentication & Access
          </CardTitle>
          <CardDescription>
            Configure authentication requirements and policies
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Two-Factor Authentication</Label>
                <p className="text-sm text-muted-foreground">
                  Require 2FA for all organization members
                </p>
              </div>
              <div className="flex items-center space-x-2">
                <Switch defaultChecked={securitySettings.twoFactorAuth} />
                <Badge variant="warning">Recommended</Badge>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Single Sign-On (SSO)</Label>
                <p className="text-sm text-muted-foreground">
                  Enable SAML/OAuth integration
                </p>
              </div>
              <div className="flex items-center space-x-2">
                <Switch defaultChecked={securitySettings.ssoEnabled} />
                <Badge variant="outline">Enterprise</Badge>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Password Policy</Label>
                <p className="text-sm text-muted-foreground">
                  Enforce strong password requirements
                </p>
              </div>
              <Switch defaultChecked={securitySettings.passwordPolicy} />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Session Management */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Clock className="mr-2 h-5 w-5" />
            Session Management
          </CardTitle>
          <CardDescription>
            Control user sessions and timeouts
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>Session Timeout</Label>
              <p className="text-sm text-muted-foreground">
                Automatically log out inactive users after {securitySettings.sessionTimeout} minutes
              </p>
            </div>
            <Button variant="outline" size="sm">
              Configure
            </Button>
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>IP Restriction</Label>
              <p className="text-sm text-muted-foreground">
                Limit access to specific IP addresses
              </p>
            </div>
            <Switch defaultChecked={securitySettings.ipRestriction} />
          </div>
        </CardContent>
      </Card>

      {/* Audit & Monitoring */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Key className="mr-2 h-5 w-5" />
            Audit & Monitoring
          </CardTitle>
          <CardDescription>
            Track and monitor security events
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>Audit Logging</Label>
              <p className="text-sm text-muted-foreground">
                Log all security-related events
              </p>
            </div>
            <Switch defaultChecked={securitySettings.auditLogging} />
          </div>

          <Button variant="outline" className="w-full">
            <Globe className="mr-2 h-4 w-4" />
            View Security Dashboard
          </Button>
        </CardContent>
      </Card>

      {/* Recent Activity */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Security Activity</CardTitle>
          <CardDescription>
            Monitor recent authentication and security events
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {recentActivity.map((activity, index) => (
              <div key={index} className="flex items-center justify-between p-3 border rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    activity.status === 'success' ? 'bg-green-100' : 'bg-red-100'
                  }`}>
                    {activity.status === 'success' ? (
                      <CheckCircle className="h-4 w-4 text-green-600" />
                    ) : (
                      <AlertTriangle className="h-4 w-4 text-red-600" />
                    )}
                  </div>
                  <div>
                    <p className="font-medium">{activity.action}</p>
                    <p className="text-sm text-muted-foreground">
                      {activity.user} from {activity.ip}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <Badge 
                    variant={activity.status === 'success' ? 'success' : 'destructive'}
                  >
                    {activity.status}
                  </Badge>
                  <p className="text-xs text-muted-foreground mt-1">
                    {activity.timestamp}
                  </p>
                </div>
              </div>
            ))}
          </div>
          
          <div className="flex justify-center mt-4">
            <Button variant="outline">
              View All Security Events
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Security Score */}
      <Card>
        <CardHeader>
          <CardTitle>Security Score</CardTitle>
          <CardDescription>
            Your organization's security posture
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center">
            <div className="text-center">
              <div className="text-4xl font-bold text-yellow-600">7/10</div>
              <p className="text-sm text-muted-foreground mt-1">Good Security</p>
              
              <div className="mt-4 space-y-2 text-left">
                <div className="flex items-center text-sm">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  Strong password policy enabled
                </div>
                <div className="flex items-center text-sm">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  Audit logging active
                </div>
                <div className="flex items-center text-sm">
                  <AlertTriangle className="h-4 w-4 text-yellow-500 mr-2" />
                  2FA not required for all users
                </div>
                <div className="flex items-center text-sm">
                  <AlertTriangle className="h-4 w-4 text-yellow-500 mr-2" />
                  SSO not configured
                </div>
              </div>
              
              <Button className="mt-4" size="sm">
                Improve Security
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Save Changes */}
      <div className="flex justify-end">
        <Button>Save Security Settings</Button>
      </div>
    </div>
  );
}